
import Vue from 'vue';
import _keyBy from 'lodash/keyBy';
import { Runnit, CompanyCustomFieldDef, RUNNITS_ACCESS_LEVEL, RUNNIT_TYPE } from '@run-diffusion/shared';
import { mapState, mapActions, mapGetters } from 'vuex';
import { FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_RUNNITS_LIMIT, RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { FiltersPanelMixin, JustAddedRowsMixin, TeamUserMixin, get$bindFirestoreOptions } from '@/mixins';
import { JUST_ADDED_LIST } from '@/utils/justAdded';
import { auth, db, Query } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { StoredLastPagination } from '@/store';
import {
	BOARDS_API_BASE_URL,
	BoardRow,
	BoardsFetchFailure,
	BoardsFetchResult,
	BoardsRequest,
	boardsFetchFailure,
	fetchBoardsPage,
} from '@/utils/boardsApi';
import {
	CursorWalk,
	MIRROR_LAG_REFETCH_MS,
	WalkFooterPagination,
	canWalkBack,
	canWalkForward,
	fetchFailureKeepsRows,
	restartWalk,
	walkAfterPage,
	walkBack,
	walkFooterPageText,
	walkFooterPagination,
	walkForward,
	walkPageNumber,
} from '@/utils/cursorWalk';
import { boardsAddedRemainingMs, onBoardsAdded } from '@/utils/boardsMirrorLag';
import RunnitBoardRow from '@/views/Runnits/pages/RunnitBoardsPage/RunnitBoardsDashboard/RunnitBoardRow.vue';
import RunnitBoardLiveDoc from '@/views/Runnits/pages/RunnitBoardsPage/RunnitBoardsDashboard/RunnitBoardLiveDoc.vue';
import TableBulkActionsPopover from '@/components/base/TableBulkActionsPopover.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import TableToolbar from '@/components/Filters/TableToolbar.vue';
import FiltersPanel from '@/components/Filters/FiltersPanel.vue';
import TeamMemberPickerField from '@/components/Filters/TeamMemberPickerField.vue';
import { normalizePanelFilters } from '@/utils/panelFilters';
import {
	getRunnitsBoardViewMode,
	setRunnitsBoardViewMode,
} from '@/utils/localStorage';
import { BoardViewMode, BoardsFilter } from '@/views/Runnits/pages/RunnitBoardsPage/types';
import {
	BOARDS_ACCESS_LEVEL_ITEMS,
	BOARDS_CUSTOM_FIELD_HEADER_PREFIX,
	BOARDS_DEFAULT_OPTIONS,
	BOARDS_RESTING_FILTERS,
	BoardsPanelFilters,
	BoardsViewItem,
	boardsViewItems,
	hydrateBoardsFilters,
	BOARDS_HEADER_VALUE,
	BOARDS_ITEMS_PER_PAGE_OPTIONS,
	BoardDownloadHeader,
	BoardDownloadRow,
	BoardsTableOptions,
	boardDownloadHeaders,
	boardDownloadRow,
	boardsDownloadSort,
	boardsFetchFailureMessage,
	boardsListIsNarrowed,
	boardsListingKey,
	buildBoardsRequest,
	bulkDeleteNotice,
	canDeleteBoard,
	hydrateBoardsOptions,
	hydrateBoardsWalk,
	rememberBoardsView,
} from './boardsListState';
import ViewModeToggle from '@/views/Runnits/pages/RunnitBoardsPage/components/ViewModeToggle.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import RunnitBoardTile from './RunnitBoardTile.vue';
import RunnitCarousel from '@/views/Runnits/RunnitCarousel.vue';
import { isWithinLimitNumRunnits } from '@/store/utils';
import { buildFeaturedTemplatesQuery, FEATURED_TEMPLATES_CAROUSEL_LIMIT } from '@/utils/featuredTemplates';

/**
 * The Boards page's list. Postgres (through api2) decides which boards exist,
 * in what order, and what a search or a filter matches; every row on screen
 * then binds its own board (RunnitBoardLiveDoc) and stays live. A company can
 * hold tens of thousands of boards, so nothing here ever loads them all.
 *
 * Cursor paged, like every other api2 listing: the endpoint says "there is
 * more" but never how many, so the footer is the walk's (see cursorWalk.ts).
 * `walk` is the position, remembered per team with the listing that produced it.
 */
export default Vue.extend({
	name: 'RunnitBoardsManagement',
	mixins: [
		TeamUserMixin,
		FiltersPanelMixin,
		JustAddedRowsMixin,
	],
	data () {
		const itemsPerPage: number = BOARDS_ITEMS_PER_PAGE_OPTIONS[0];
		return {
			RUNNITS_OWNER_SELECTION,
			RUNNIT_TYPE,
			options: { ...BOARDS_DEFAULT_OPTIONS, itemsPerPage } as BoardsTableOptions,
			itemsPerPageOptions: BOARDS_ITEMS_PER_PAGE_OPTIONS,
			didHydrateLastPagination: false,
			// Kept across pages; a new search, filter or account clears it.
			selectedRows: [] as BoardRow[],
			// Boards deleted (or moved away) since the list was fetched, by id: not selectable
			removedRowIds: {} as Record<string, true>,
			// The search applies as it is typed; the Filters box's fields wait for Apply.
			// Both are remembered per team with the rest of the view.
			searchQuery: '',
			appliedFilters: { ...BOARDS_RESTING_FILTERS } as BoardsPanelFilters,
			accessLevelFilterItems: BOARDS_ACCESS_LEVEL_ITEMS,
			viewMode: (getRunnitsBoardViewMode() as BoardViewMode) || BoardViewMode.TABLE,
			featuredBoardTemplates: [] as Runnit[],
			loadingFeaturedBoardTemplates: false,

			rows: [] as BoardRow[],
			// Where the walk is. Every filter, search, sort or page-size change
			// restarts it, because a cursor belongs to one listing and the
			// endpoint refuses it in any other.
			walk: restartWalk() as CursorWalk,
			// The walk and page size that produced the rows on screen, which is
			// what the footer describes while the next page loads.
			shownPage: {
				walk: restartWalk(),
				itemsPerPage,
			} as { walk: CursorWalk; itemsPerPage: number },
			loading: false,
			failed: false,
			// Bumped on every request so a slow, superseded response is dropped
			// instead of overwriting a newer page.
			requestVersion: 0,
			fetchTimer: null as number | null,
			// True from a user-initiated fetch until a fetch completes. A
			// background refetch that supersedes one answers for it.
			foregroundFetchPending: false,
			// The one pending catch-up refetch after a change, if any.
			mirrorLagRefetchTimer: null as number | null,
			stopListeningToBoardChanges: null as (() => void) | null,
			isDestroyed: false,
			loadingBulkAccess: false,
			// Bulk delete: the boards it acts on, fixed when the dialog opens, and how many were selected
			bulkDeleteOpen: false,
			bulkDeleteRows: [] as BoardRow[],
			bulkDeleteSelectedCount: 0,
			deletingBulkBoards: false,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'runnitBoardDashboard',
			'loadingLimitNumRunnits',
			'limitNumRunnits',
		]),
		...mapGetters([
			'displayLabels',
		]),
		isTeamMode (): boolean {
			return this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM;
		},
		// The account the list reads: the selected team, or none for personal boards
		accountTeamId (): string | null {
			return this.isTeamMode ? (this.team?.id || null) : null;
		},
		// JustAddedRowsMixin: a board created here shows on top before the listing has it,
		// on its Team's list or its owner's own
		justAddedListKey (): string | null {
			if (this.isTeamMode) return this.accountTeamId ? JUST_ADDED_LIST.teamBoards(this.accountTeamId) : null;
			return this.user?.id ? JUST_ADDED_LIST.personalBoards(this.user.id) : null;
		},
		// Who is asking, and as which account, is known
		listingReady (): boolean {
			return !!(this.user?.id && (!this.isTeamMode || this.accountTeamId));
		},
		showBoardsLimitUpsellCounter (): boolean {
			return !!(
				this.loadingLimitNumRunnits === false &&
				!this.user.isAdmin &&
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER &&
				!this._get(this.user, 'clubInfo.limit.unlimitedRunnits')
			);
		},
		boardsLimitNum (): number {
			return this._get(this.user, 'clubInfo.limit.numRunnitsLimit') || FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_RUNNITS_LIMIT;
		},
		boardsUsedCount (): number {
			return (this.limitNumRunnits || []).length;
		},
		isAtBoardsLimit (): boolean {
			return this.boardsUsedCount >= this.boardsLimitNum;
		},
		boardsUsedDisplay (): string {
			const count: number = this.boardsUsedCount;
			return count > this.boardsLimitNum
				? `${this.boardsLimitNum}+/${this.boardsLimitNum}`
				: `${count}/${this.boardsLimitNum}`;
		},
		itemsPerPage (): number {
			return this.options.itemsPerPage || this.itemsPerPageOptions[0];
		},
		runnitCustomFieldDefs (): CompanyCustomFieldDef[] {
			return this.team?.company?.runnitCustomFieldDefs || [];
		},
		newBoardMenuItems () {
			return [
				{
					id: 1,
					icon: 'mdi-plus-circle-outline',
					label: 'From Scratch',
					description: 'Create a new board from scratch',
					value: 'from-scratch',
				},
				{
					id: 2,
					icon: 'mdi-file-document-outline',
					label: 'From Template',
					description: 'Create a board from an existing template',
					value: 'from-template',
				},
			];
		},
		searchPlaceholder (): string {
			const customField: string = this.isTeamMode && this.runnitCustomFieldDefs.length
				? this.runnitCustomFieldDefs[0].label
				: 'description';
			return `Search by Name, ${customField}...`;
		},
		// FiltersPanelMixin: the box at rest, and what is applied
		restingPanelFilters (): BoardsPanelFilters {
			return BOARDS_RESTING_FILTERS;
		},
		appliedPanelFilters (): BoardsPanelFilters {
			return this.appliedFilters;
		},
		viewFilterItems (): BoardsViewItem[] {
			return boardsViewItems(!!this.hasManageAllGenerationsPermission, this.displayLabels.team);
		},
		// The View field shows the icon of the view it holds, as the option does in the list
		draftViewIcon (): string {
			const item: BoardsViewItem | undefined = this.viewFilterItems.find((option: BoardsViewItem) => option.value === this.draftFilters.view);
			return item ? item.icon : 'mdi-view-dashboard-outline';
		},
		// Everyone's boards only with the permission to see them; a remembered one without it reads as All
		requestFilters (): BoardsPanelFilters {
			if (this.appliedFilters.view === BoardsFilter.EVERYONES_BOARDS && !this.hasManageAllGenerationsPermission) {
				return { ...this.appliedFilters, view: BoardsFilter.ALL };
			}
			return this.appliedFilters;
		},
		// The owners' addresses the rows on screen carry, so a chosen owner reads as a person
		ownerLabels (): Record<string, string> {
			const labels: Record<string, string> = {};
			this.rows.forEach((row: BoardRow) => {
				if (row.ownerUserId && row.ownerEmail) labels[row.ownerUserId] = row.ownerEmail;
			});
			return labels;
		},
		// The request for the page the walk is on
		boardsRequest (): BoardsRequest {
			return buildBoardsRequest({
				teamId: this.accountTeamId,
				cursor: this.walk.cursor,
				options: this.options,
				searchQuery: this.searchQuery,
				filters: this.requestFilters,
				itemsPerPage: this.itemsPerPage,
			});
		},
		// Everything a cursor belongs to, so a remembered one is only reused in its own listing
		listingKey (): string {
			return boardsListingKey(this.boardsRequest);
		},
		// Who is asking, and as which account: a change is a new listing from the top
		accountSignature (): string {
			return JSON.stringify([this.user?.id || null, this.isTeamMode, this.accountTeamId]);
		},
		isNarrowed (): boolean {
			return boardsListIsNarrowed(this.searchQuery, this.isTeamMode ? this.appliedFilters : null);
		},
		showBulkAccessSettings (): boolean {
			return !!(this.isTeamMode && (this.hasShareRunnitAssetsPermission || this.hasManageAllGenerationsPermission));
		},
		/** The selected boards the caller may delete: Delete shows for any, and acts on these alone. */
		deletableSelectedRows (): BoardRow[] {
			const userId: string | null = this._get(this.user, 'id') || null;
			return this.selectedRows.filter((row: BoardRow) => (
				canDeleteBoard(row.ownerUserId, userId, !!this.hasManageAllGenerationsPermission)
			));
		},
		bulkDeleteText (): string {
			const count: number = this.bulkDeleteRows.length;
			return `Delete ${count} ${count === 1 ? 'Board' : 'Boards'}`;
		},
		bulkDeleteSkippedCount (): number {
			return Math.max(0, this.bulkDeleteSelectedCount - this.bulkDeleteRows.length);
		},
		/**
		 * The rows as the table gets them. A deleted board's row is marked as one the
		 * table may not select (Vuetify's `isSelectable`), so select-all leaves it out.
		 */
		tableRows (): BoardRow[] {
			if (!Object.keys(this.removedRowIds).length) return this.listedRows;
			return this.listedRows.map((row: BoardRow) => (
				this.removedRowIds[row.id] ? { ...row, isSelectable: false } : row
			));
		},
		// The CSV is built from the Postgres rows alone: the selection is a list of them.
		downloadRows (): BoardDownloadRow[] {
			return this.selectedRows.map((row: BoardRow) => boardDownloadRow(row));
		},
		downloadHeaders (): BoardDownloadHeader[] {
			return boardDownloadHeaders(this.selectedRows, this.isTeamMode);
		},
		downloadSort (): { sortBy: string[]; sortDesc: boolean[] } {
			return boardsDownloadSort(this.options);
		},
		// The popover turns anything but letters and digits into dashes: "Acme Studio" saves as Acme-Studio-Boards.csv
		downloadFileName (): string {
			const teamName: string = this.isTeamMode ? (this.team?.name || '') : '';
			return teamName ? `${teamName}-Boards` : 'Boards';
		},
		canWalkBack (): boolean {
			return canWalkBack(this.walk);
		},
		footerPagination (): WalkFooterPagination {
			return walkFooterPagination(this.shownPage.walk, this.shownPage.itemsPerPage, this.rows.length);
		},
		// The footer reads the page and the page size, and hands back a copy with one of them changed
		footerOptions (): BoardsTableOptions & { page: number } {
			return {
				...this.options,
				page: this.footerPagination.page,
				itemsPerPage: this.itemsPerPage,
			};
		},
		footerPageText (): string {
			return walkFooterPageText(this.shownPage.walk, this.footerPagination);
		},
		headers () {
			const baseHeaders = [
				{
					text: 'Name',
					value: BOARDS_HEADER_VALUE.TITLE,
					align: 'left',
					class: 'py-0 pl-0 pr-1',
					sortable: true,
				},
				{
					text: 'Description',
					value: BOARDS_HEADER_VALUE.DESCRIPTION,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
			];

			// Custom fields belong to the company, so a team's boards have them and personal ones do not
			const customFieldHeaders = this.isTeamMode
				? this.runnitCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => ({
					text: fieldDef.label,
					value: `${BOARDS_CUSTOM_FIELD_HEADER_PREFIX}${fieldDef.key}`,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				}))
				: [];

			const metaHeaders = [
				{
					text: 'Created',
					value: BOARDS_HEADER_VALUE.CREATED,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
			];

			// Every personal board is your own and private, so only a team's boards show these
			const ownerHeaders = this.isTeamMode ? [
				{
					text: 'Owner',
					value: BOARDS_HEADER_VALUE.OWNER,
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
				},
				{
					text: 'Access',
					value: BOARDS_HEADER_VALUE.ACCESS,
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
				},
			] : [];

			const actionHeaders = [
				{
					text: 'Actions',
					value: BOARDS_HEADER_VALUE.ACTIONS,
					align: 'right',
					class: 'py-0 pr-4',
					sortable: false,
				},
			];

			return [
				...baseHeaders,
				...customFieldHeaders,
				...metaHeaders,
				...ownerHeaders,
				...actionHeaders,
			];
		},
		isTableView (): boolean {
			return this.viewMode === BoardViewMode.TABLE;
		},
		carouselItemsWithNewBoard () {
			const placeholder = { isNewBoardPlaceholder: true };
			return [placeholder, ...this.featuredBoardTemplates];
		},
	},
	watch: {
		/**
		 * A different person or account is a different listing: back to the top,
		 * with nothing selected. The first time an account is known, its
		 * remembered search, filters and position come back first.
		 */
		accountSignature: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal === oldVal) return;
				if (oldVal !== undefined) this.clearSelectedRows();
				this.walk = restartWalk();
				this.hydrateLastPagination();
				this.scheduleFetch();
			},
		},
		/**
		 * Everyone's boards is asked for only with the permission to see them
		 * (requestFilters), and the permission can arrive after the first page,
		 * or go. Applied, that view is then a different listing: back to the top.
		 * Watched here rather than through requestFilters, so that restoring a
		 * remembered view does not restart the position it came back with.
		 */
		hasManageAllGenerationsPermission (newVal: boolean, oldVal: boolean) {
			if (!!newVal === !!oldVal) return;
			if (this.appliedFilters.view !== BoardsFilter.EVERYONES_BOARDS) return;
			this.clearSelectedRows();
			this.resetToFirstPage();
		},
	},
	async created () {
		// A board added anywhere in the app: the list catches up once the mirror has it. An edit
		// refetches nothing: the board follows it live and stays where it is until Refresh.
		this.stopListeningToBoardChanges = onBoardsAdded(() => this.refetchAfterMirrorLag());
		// One added just before this page opened (create a board, open it, come straight back)
		// is not in the mirror yet either: ask again once it will be.
		const remainingLagMs: number = boardsAddedRemainingMs();
		if (remainingLagMs > 0) this.refetchAfterMirrorLag(remainingLagMs);

		await this.loadFeaturedBoardTemplates();
	},
	beforeDestroy () {
		this.isDestroyed = true;
		if (this.fetchTimer !== null) {
			window.clearTimeout(this.fetchTimer);
			this.fetchTimer = null;
		}
		this.clearMirrorLagRefetch();
		if (this.stopListeningToBoardChanges) {
			this.stopListeningToBoardChanges();
			this.stopListeningToBoardChanges = null;
		}
		if (this.team?.id) {
			// The position travels with the listing that produced it: a cursor
			// is only meaningful there.
			this.storeLastPagination({
				key: 'lastRunnitBoardsPagination',
				pagination: rememberBoardsView(this.options, this.listingKey, this.walk.cursor, walkPageNumber(this.walk), this.searchQuery, this.appliedFilters),
				teamId: this.team.id,
			});
		}
	},
	methods: {
		// Reuses the same dialog the create-board flow raises, so the limit copy lives in one place
		onBoardsLimitLearnMore () {
			this.setUpsellDialog({ runnitIsAtNumRunnitsLimit: true });
		},
		...mapActions([
			'storeLastPagination',
			'setUpsellDialog',
			'updateSnackbar',
		]),
		isWithinLimitNumRunnits,
		onNewBoardMenuSelected (menuValue: string) {
			switch (menuValue) {
				case 'from-scratch':
					this.$emit('new-runnit');
					break;
				case 'from-template':
					this.$router.push({ name: 'RunnitBoardTemplatesPage' });
					break;
				default:
					break;
			}
		},
		setViewMode (mode: BoardViewMode) {
			try {
				this.viewMode = mode;
				setRunnitsBoardViewMode(mode);
			} catch (error) {
				console.warn('Failed to save view mode preference:', error);
				this.viewMode = mode;
			}
		},
		// FiltersPanelMixin: Apply and Clear all land here
		commitPanelFilters (next: BoardsPanelFilters) {
			this.clearSelectedRows();
			this.appliedFilters = normalizePanelFilters(BOARDS_RESTING_FILTERS, next);
			this.resetToFirstPage();
		},
		clearSelectedRows () {
			this.$nextTick(() => {
				// Next tick seems to prevent issue with check boxes getting in a state where they're unselectable after a bulk update and clear selected items operation
				this.selectedRows = [];
			});
		},
		/**
		 * The sort, page size and position this account had when the page was
		 * last left. Once per visit, and only when the account is known: the
		 * sort must name a column this company's table has, and the position is
		 * resumed only in the very listing it was taken in.
		 */
		hydrateLastPagination () {
			if (this.didHydrateLastPagination || !this.team?.id) return;
			const stored: StoredLastPagination | undefined = this.runnitBoardDashboard?.lastRunnitBoardsPagination?.[this.team.id];
			this.didHydrateLastPagination = true;
			if (!stored) return;
			// The search and box first: the remembered position is resumed only in the listing they make
			const restored: { searchQuery: string; filters: BoardsPanelFilters } | null = hydrateBoardsFilters(stored);
			if (restored) {
				this.searchQuery = restored.searchQuery;
				this.appliedFilters = restored.filters;
				this.syncFiltersPanel();
			}
			this.options = hydrateBoardsOptions(
				stored,
				this.options,
				this.itemsPerPageOptions,
				this.isTeamMode ? this.runnitCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => fieldDef.key) : [],
			);
			this.walk = hydrateBoardsWalk(stored, this.listingKey);
		},
		/**
		 * Coalesce the bursts the table produces (its own mount-time emit, a
		 * page reset followed by the echoed options) into one request. The
		 * version guard in fetchPage covers anything that still overlaps.
		 */
		scheduleFetch () {
			if (this.fetchTimer !== null) return;
			this.fetchTimer = window.setTimeout(() => {
				this.fetchTimer = null;
				this.fetchPage();
			}, 0);
		},
		refetch (): Promise<void> {
			return this.fetchPage();
		},
		/**
		 * A board added, or a row found deleted, changes which rows exist, and
		 * that can only catch up once the mirror has the write. At most one timer
		 * is pending: a repeat call restarts it, so the refetch is a full lag after
		 * the latest change.
		 */
		refetchAfterMirrorLag (delayMs: number = MIRROR_LAG_REFETCH_MS) {
			if (this.isDestroyed) return;
			this.clearMirrorLagRefetch();
			this.mirrorLagRefetchTimer = window.setTimeout(() => {
				this.mirrorLagRefetchTimer = null;
				if (this.isDestroyed) return;
				this.fetchPage(true);
			}, delayMs);
		},
		clearMirrorLagRefetch () {
			if (this.mirrorLagRefetchTimer === null) return;
			window.clearTimeout(this.mirrorLagRefetchTimer);
			this.mirrorLagRefetchTimer = null;
		},
		// A row's board was deleted since the list was fetched: it can no longer be selected, and it
		// drops off the list once the mirror knows
		onBoardRemoved (runnitId: string) {
			this.removedRowIds = { ...this.removedRowIds, [runnitId]: true };
			this.forgetJustAddedRow(runnitId);
			if (this.selectedRows.some((row: BoardRow) => row.id === runnitId)) {
				this.selectedRows = this.selectedRows.filter((row: BoardRow) => row.id !== runnitId);
			}
			this.refetchAfterMirrorLag();
		},
		/**
		 * `background` is the catch-up refetch nobody asked for: if it fails the
		 * table keeps the rows it has (they are live through their bindings)
		 * and only the snackbar says so, unless it superseded a fetch the user
		 * did ask for.
		 */
		async fetchPage (background: boolean = false): Promise<void> {
			if (!this.listingReady) {
				// Nothing to ask for yet. Supersede any request still in flight,
				// or the previous account's boards would land under this one.
				// Still loading while the person or their team is, so the empty
				// state does not flash up first.
				this.requestVersion++;
				this.foregroundFetchPending = false;
				this.loading = !!(this.loadingUser || this.loadingTeam);
				this.failed = false;
				this.walk = restartWalk();
				this.showRows([]);
				return;
			}

			const version: number = ++this.requestVersion;
			if (!background) this.foregroundFetchPending = true;
			this.loading = true;
			this.failed = false;
			const request: BoardsRequest = this.boardsRequest;

			let result: BoardsFetchResult;
			try {
				const currentUser = auth.currentUser;
				const idToken: string | null = currentUser ? await currentUser.getIdToken() : null;
				result = idToken
					? await fetchBoardsPage({ baseUrl: BOARDS_API_BASE_URL, idToken, request })
					: { ok: false, reason: 'unauthorized' };
			} catch (e) {
				console.error('Error loading boards:', e);
				result = { ok: false, reason: 'failed' };
			}

			// A newer request superseded this one; let it own the table. And a
			// page that is gone owns nothing.
			if (this.isDestroyed || version !== this.requestVersion) return;

			this.loading = false;
			const supersededForeground: boolean = this.foregroundFetchPending;
			this.foregroundFetchPending = false;
			if (result.ok) {
				this.walk = walkAfterPage(this.walk, result.page);
				this.showRows(result.page.rows);
				return;
			}

			// The position we resumed from is not in this listing any more, which
			// a remembered cursor can become. Start the walk over.
			const failure: BoardsFetchFailure | null = boardsFetchFailure(result);
			if (failure && failure.reason === 'stale-cursor' && this.walk.cursor) {
				this.walk = restartWalk();
				this.scheduleFetch();
				return;
			}

			if (!fetchFailureKeepsRows({ background, supersededForeground, hasRows: !!this.rows.length })) {
				this.failed = true;
				this.walk = restartWalk();
				this.showRows([]);
			}
			this.notifyFetchFailure(result);
		},
		// Rows go on screen together with the walk and page size that produced them.
		showRows (rows: BoardRow[]) {
			this.rows = rows;
			this.shownPage = {
				walk: this.walk,
				itemsPerPage: this.itemsPerPage,
			};
			// A selected board back on screen is selected as this copy, so the CSV reads the newer one.
			const shownById: Record<string, BoardRow> = _keyBy(rows, 'id');
			if (this.selectedRows.some((row: BoardRow) => shownById[row.id] && shownById[row.id] !== row)) {
				this.selectedRows = this.selectedRows.map((row: BoardRow) => shownById[row.id] || row);
			}
		},
		notifyFetchFailure (result: BoardsFetchResult) {
			if (!boardsFetchFailure(result)) return;
			const messageByReason: Record<string, string> = {
				unauthorized: 'Your session has expired, sign in again to view boards',
				forbidden: this.requestFilters.view === BoardsFilter.EVERYONES_BOARDS
					? 'You do not have permission to see everyone\'s boards'
					: 'You do not have access to these boards',
				network: 'Could not reach the server, check your connection and try again',
				failed: 'Could not load boards, please try again',
			};
			this.updateSnackbar({
				status: SNACKBAR_STATUS.ERROR,
				message: boardsFetchFailureMessage(result, messageByReason),
				show: true,
			});
		},
		/**
		 * Back to the start of the walk, and fetch. Every search, filter and
		 * sort change does this: a cursor is a position in one listing, and the
		 * endpoint refuses it in another.
		 */
		resetToFirstPage () {
			this.walk = restartWalk();
			this.scheduleFetch();
		},
		/**
		 * The table hands back its options when a header is clicked, and once
		 * when it mounts. Only the sort is the table's (the footer owns the page
		 * and its size), and a new sort is a new listing, so the walk restarts.
		 * Handled here rather than watched: a sort restored from the store
		 * arrives with the position it belongs to and must not restart it.
		 */
		onTableOptions (next: BoardsTableOptions) {
			const sortBy: string[] = Array.isArray(next.sortBy) ? next.sortBy : [];
			const sortDesc: boolean[] = Array.isArray(next.sortDesc) ? next.sortDesc : [];
			if (JSON.stringify([sortBy, sortDesc]) === JSON.stringify([this.options.sortBy, this.options.sortDesc])) return;
			this.options = { ...this.options, sortBy, sortDesc };
			this.resetToFirstPage();
		},
		setItemsPerPage (itemsPerPage: number) {
			// Restart too: resuming mid-walk with a different size shows a page
			// that starts in an arbitrary place.
			this.options = { ...this.options, itemsPerPage };
			this.resetToFirstPage();
		},
		goToNextPage () {
			if (!canWalkForward(this.walk)) return;
			this.walk = walkForward(this.walk);
			this.scheduleFetch();
		},
		goToPreviousPage () {
			if (!canWalkBack(this.walk)) return;
			this.walk = walkBack(this.walk);
			this.scheduleFetch();
		},
		/**
		 * The footer hands back its options with one thing changed: the page
		 * size (a new walk from the top), or the page, one either way. A page
		 * restored from the store has no cursors behind it, so its Back starts
		 * the walk over rather than doing nothing.
		 */
		onFooterOptions (next: { page?: number; itemsPerPage?: number }) {
			if (next.itemsPerPage && next.itemsPerPage !== this.footerOptions.itemsPerPage) {
				this.setItemsPerPage(next.itemsPerPage);
				return;
			}
			if (!next.page || next.page === this.footerOptions.page) return;
			if (next.page > this.footerOptions.page) {
				this.goToNextPage();
			} else if (canWalkBack(this.walk)) {
				this.goToPreviousPage();
			} else {
				this.resetToFirstPage();
			}
		},
		canEditRunnit (runnit: Runnit): boolean {
			// For featured templates, only admins can edit/delete
			if (runnit?.type === RUNNIT_TYPE.TEMPLATE && runnit?.isFeatured) {
				return !!this.user?.isAdmin;
			}
			return !!(
				runnit &&
				(
					this.hasManageAllGenerationsPermission ||
					(
						runnit.userId === this.user.id ||
						(
							[RUNNITS_ACCESS_LEVEL.SHARED, RUNNITS_ACCESS_LEVEL.RESTRICTED].includes(runnit.accessLevel) &&
							this.hasShareRunnitAssetsPermission
						)
					)
				)
			);
		},
		onEditRunnit (runnit: Runnit) {
			this.$emit('edit-runnit', runnit);
		},
		onOpenRunnit (runnit: Runnit) {
			this.$emit('open-runnit', runnit);
		},
		onDeleteRunnit (runnit: Runnit) {
			this.$emit('delete-runnit', runnit);
		},
		/**
		 * The bulk access dialog edits the boards themselves, so it gets them as
		 * they are now, read by id, rather than the mirror's rows, which can be a
		 * few seconds behind. The selection clears once the change is saved, and the
		 * rows follow it live where they are until Refresh.
		 */
		async onBulkAccessSettings () {
			if (!this.selectedRows.length || this.loadingBulkAccess) return;
			this.loadingBulkAccess = true;
			try {
				const runnits: Runnit[] = await this.fetchRunnitsById(this.selectedRows.map((row: BoardRow) => row.id));
				if (this.isDestroyed) return;
				if (!runnits.length) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'The selected boards are no longer available',
						show: true,
					});
					return;
				}
				this.$emit('edit-runnit-access', {
					runnits,
					onSuccess: () => this.clearSelectedRows(),
				});
			} catch (error) {
				console.error('Error loading the selected boards:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Could not load the selected boards, please try again',
					show: true,
				});
			} finally {
				this.loadingBulkAccess = false;
			}
		},
		openBulkDelete () {
			if (!this.deletableSelectedRows.length) return;
			// Fixed now: the selection shrinks as deleted rows leave it, and the dialog must not follow
			this.bulkDeleteRows = this.deletableSelectedRows;
			this.bulkDeleteSelectedCount = this.selectedRows.length;
			this.bulkDeleteOpen = true;
		},
		/**
		 * Deletes the boards the dialog named, the way a board's own Delete does: it
		 * marks each one deleted. Each is read fresh first, so one deleted elsewhere
		 * meanwhile counts as gone, and one the caller may no longer delete is left
		 * alone. The rows dim to Deleted as their bindings see it, and leave the list
		 * once the mirror has caught up.
		 */
		async onBulkDeleteConfirm (closeAndResetCallback?: () => void) {
			const targets: BoardRow[] = this.bulkDeleteRows;
			this.deletingBulkBoards = true;
			try {
				const runnits: Runnit[] = await this.fetchRunnitsById(targets.map((row: BoardRow) => row.id));
				const userId: string | null = this._get(this.user, 'id') || null;
				const deletable: Runnit[] = runnits.filter((runnit: Runnit) => (
					runnit.type === RUNNIT_TYPE.WORKFLOW &&
					canDeleteBoard(runnit.userId, userId, !!this.hasManageAllGenerationsPermission)
				));
				const deletedAt: Date = new Date();
				const outcomes: boolean[] = await Promise.all(deletable.map((runnit: Runnit) => (
					db.doc(`runnits/${runnit.id}`).update({ deletedAt }).then(
						() => true,
						(error: unknown) => {
							console.error('Error deleting board', runnit.id, error);
							return false;
						},
					)
				)));

				// fetchRunnitsById leaves out a board already deleted, which is as good as this one
				const alreadyGone: number = targets.length - runnits.length;
				const notice: { ok: boolean; message: string } = bulkDeleteNotice(targets.length, alreadyGone + outcomes.filter(Boolean).length);
				this.updateSnackbar({
					status: notice.ok ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
					message: notice.message,
					show: true,
				});
				if (notice.ok) this.clearSelectedRows();
			} catch (error) {
				console.error('Error deleting boards:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error! Problem deleting the boards, please try again.',
					show: true,
				});
			} finally {
				this.deletingBulkBoards = false;
				this.bulkDeleteOpen = false;
				if (closeAndResetCallback) closeAndResetCallback();
			}
		},
		async fetchRunnitsById (runnitIds: string[]): Promise<Runnit[]> {
			const snapshots = await Promise.all(runnitIds.map((runnitId: string) => db.doc(`runnits/${runnitId}`).get()));
			return snapshots
				.filter((snapshot) => snapshot.exists && !(snapshot.data() as Runnit).deletedAt)
				.map((snapshot) => ({ ...(snapshot.data() as Runnit), id: snapshot.id }));
		},
		setSearchQuery (query: string) {
			const next: string = query || '';
			if (next === this.searchQuery) return;
			this.clearSelectedRows();
			this.searchQuery = next;
			this.resetToFirstPage();
		},
		onViewTemplatesClick () {
			this.$router.push({ name: 'RunnitBoardTemplatesPage' });
		},
		async loadFeaturedBoardTemplates () {
			this.loadingFeaturedBoardTemplates = true;
			try {
				// Featured templates in the curated order set in Admin > Featured Templates
				// index created: runnits - deletedAt Ascending isFeatured Ascending type Ascending featuredOrder Ascending __name__ Ascending
				const runnitsRef: Query = buildFeaturedTemplatesQuery(
					db.collection('runnits'),
					FEATURED_TEMPLATES_CAROUSEL_LIMIT,
				);

				const newResult: Runnit[] = await this.$bind(
					'featuredBoardTemplates',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.featuredBoardTemplates = [];
				}
			} catch (error) {
				console.error('Error loading featured board templates:', error);
				this.featuredBoardTemplates = [];
			} finally {
				this.loadingFeaturedBoardTemplates = false;
			}
		},
	},
	components: {
		RunnitBoardRow,
		RunnitBoardLiveDoc,
		TableBulkActionsPopover,
		DoubleConfirmDangerDialog,
		TableToolbar,
		FiltersPanel,
		TeamMemberPickerField,
		ViewModeToggle,
		LoadingState,
		BaseButton,
		BaseStyledMenu,
		RunnitBoardTile,
		RunnitCarousel,
	},
});
